<app-order-modal-stage-item-icon
  [status]="stageStatus()"
  [icon]="icon()"
></app-order-modal-stage-item-icon>

@let isInProgress = stageStatus() === OrderStageStatus.IN_PROGRESS;
@let isSkipped = stageStatus() === OrderStageStatus.SKIPPED;
@let isCompleted = stageStatus() === OrderStageStatus.COMPLETED;

<div class="information f-body-m f-medium">
  @switch (stage()) {
    @case (OrderStage.CREATION) {
      {{
        `${I18N}.${isDraft() ? 'draftCreation' : 'orderCreation'}` | translate
      }}

      @let author = order().author;
      @if (author && order().timestamps.createdAt; as date) {
        <div class="f-body-xs f-muted">
          <ng-container
            *ngTemplateOutlet="dateTemplate; context: { date }"
          ></ng-container>

          <ng-container
            *ngTemplateOutlet="authorLinkTemplate; context: { author }"
          ></ng-container>
        </div>
      }
    }

    @case (OrderStage.ROUTE_VERIFICATION) {
      {{ `${I18N}.routeVerification` | translate }}

      @if (isSkipped) {
        <ng-container *ngTemplateOutlet="skippedTemplate"></ng-container>
      } @else if (order().route; as route) {
        <div class="f-body-xs f-muted">
          <ng-container
            *ngTemplateOutlet="dateTemplate; context: { date: route.updatedAt }"
          ></ng-container>

          <ng-container
            *ngTemplateOutlet="
              authorLinkTemplate;
              context: { author: route.author }
            "
          ></ng-container>
        </div>
      }
    }

    @case (OrderStage.VEHICLE_SEARCH) {
      {{ `${I18N}.vehicleSearch` | translate }}

      @if (isSkipped) {
        <ng-container *ngTemplateOutlet="skippedTemplate"></ng-container>
      } @else if (order().timestamps.vehicleSearchAt; as start) {
        <div class="f-body-xs f-muted">
          @let outsourcingEnd =
            order().outsourcingData && order().timestamps.toDoAt;
          @let end = order().timestamps.toAcceptAt || outsourcingEnd;

          <ng-container
            *ngTemplateOutlet="progressTemplate; context: { start, end }"
          ></ng-container>
        </div>
      }
    }

    @case (OrderStage.ENGINE_PROPOSAL) {
      {{ `${I18N}.vehicleProposalAcceptance` | translate }}

      @if (order().timestamps.toDoAt; as date) {
        <div class="f-body-xs f-muted">
          <ng-container
            *ngTemplateOutlet="dateTemplate; context: { date }"
          ></ng-container>

          @if (acceptedProposal()?.acceptance?.author; as author) {
            <ng-container
              *ngTemplateOutlet="authorLinkTemplate; context: { author }"
            ></ng-container>
          }
        </div>
      }
    }

    @case (OrderStage.MANUAL_PROPOSAL) {
      {{ `${I18N}.manualVehicleSelection` | translate }}

      @if (order().timestamps.toDoAt; as date) {
        <div class="f-body-xs f-muted">
          <ng-container
            *ngTemplateOutlet="dateTemplate; context: { date }"
          ></ng-container>

          @if (acceptedProposal()?.acceptance?.author; as author) {
            <ng-container
              *ngTemplateOutlet="authorLinkTemplate; context: { author }"
            ></ng-container>
          }
        </div>
      }
    }

    @case (OrderStage.OUTSOURCING) {
      {{ `${I18N}.outsourcingSelection` | translate }}

      @if (order().timestamps.toDoAt; as date) {
        <div class="f-body-xs f-muted">
          <ng-container
            *ngTemplateOutlet="dateTemplate; context: { date }"
          ></ng-container>

          @if (order().outsourcingData?.author; as author) {
            <ng-container
              *ngTemplateOutlet="authorLinkTemplate; context: { author }"
            ></ng-container>
          }
        </div>
      }
    }

    @case (OrderStage.REALIZATION) {
      {{ `${I18N}.orderRealization` | translate }}

      @if (isSkipped) {
        <ng-container *ngTemplateOutlet="skippedTemplate"></ng-container>
      } @else if (order().timestamps.inProgressAt; as start) {
        <div class="f-body-xs f-muted">
          <ng-container
            *ngTemplateOutlet="
              progressTemplate;
              context: { start, end: order().timestamps.completingDocumentsAt }
            "
          ></ng-container>
        </div>
      }
    }

    @case (OrderStage.COMPLETING_DOCUMENTS) {
      {{ `${I18N}.completingDocuments` | translate }}

      @if (isSkipped) {
        <ng-container *ngTemplateOutlet="skippedTemplate"></ng-container>
      } @else if (order().timestamps.completingDocumentsAt; as start) {
        <div class="f-body-xs f-muted">
          <ng-container
            *ngTemplateOutlet="
              progressTemplate;
              context: { start, end: order().timestamps.invoiceToIssueAt }
            "
          ></ng-container>
        </div>
      }
    }

    @case (OrderStage.WAITING_FOR_COLLECTIVE_INVOICE) {
      {{ `${I18N}.waitingForCollectiveInvoice` | translate }}

      <p class="f-body-xs f-muted">
        <app-contractor-invoice-collective-issue-term
          [invoice]="order().payment.contractorInvoice"
          label="labels.term"
        ></app-contractor-invoice-collective-issue-term>
      </p>
    }

    @case (OrderStage.INVOICE_TO_ISSUE) {
      {{ `${I18N}.invoiceIssuing` | translate }}

      @if (isSkipped) {
        <ng-container *ngTemplateOutlet="skippedTemplate"></ng-container>
      } @else if (order().timestamps.invoiceToIssueAt; as start) {
        <div class="f-body-xs f-muted">
          <ng-container
            *ngTemplateOutlet="
              progressTemplate;
              context: { start, end: order().timestamps.waitingForPaymentAt }
            "
          ></ng-container>
        </div>
      }
    }

    @case (OrderStage.PAYMENT_CONFIRMATION) {
      {{ `${I18N}.paymentConfirmation` | translate }}

      @if (isSkipped) {
        <ng-container *ngTemplateOutlet="skippedTemplate"></ng-container>
      } @else if (order().timestamps.paidAt; as date) {
        <ng-container
          *ngTemplateOutlet="dateTemplate; context: { date }"
        ></ng-container>
      }
    }
  }
</div>

<ng-template let-author="author" #authorLinkTemplate>
  ·
  <onyx-link
    color="gray"
    font="f-body-xs"
    (click)="employeeHelper.openModal(author)"
  >
    {{ author | onyxName }}
  </onyx-link>
</ng-template>

<ng-template #skippedTemplate>
  <div class="f-body-xs f-muted">
    {{ 'labels.skipped' | translate }}
  </div>
</ng-template>

<ng-template let-date="date" #dateTemplate>
  <span
    class="f-body-xs f-muted"
    [innerHTML]="date | onyxDate: 'time-date' : { separator: ' · ' }"
  ></span>
</ng-template>

<ng-template let-date="date" #inProgressDateTemplate>
  <ng-container
    *ngTemplateOutlet="dateTemplate; context: { date }"
  ></ng-container>

  <span class="f-body-xs f-muted">
    →
    {{ 'labels.now' | translate }}
  </span>
</ng-template>

<ng-template let-start="start" let-end="end" #durationTemplate>
  <span class="f-body-xs f-muted">
    @if (CommonHelper.isSameDay(start, end)) {
      {{ start | onyxTime }}-{{ end | onyxTime }} ·
      {{ end | onyxDate: 'date' }}
    } @else {
      <ng-container
        *ngTemplateOutlet="dateTemplate; context: { date: start }"
      ></ng-container>
      -
      <ng-container
        *ngTemplateOutlet="dateTemplate; context: { date: end }"
      ></ng-container>
    }
  </span>
</ng-template>

<ng-template let-start="start" let-end="end" #progressTemplate>
  @if (isCompleted && end) {
    <ng-container
      *ngTemplateOutlet="durationTemplate; context: { start, end }"
    ></ng-container>
  } @else if (isInProgress) {
    <ng-container
      *ngTemplateOutlet="inProgressDateTemplate; context: { date: start }"
    ></ng-container>
  }
</ng-template>
